<script>
    import {onMount} from 'svelte';

    import {slide} from 'svelte/transition';
    import {cubicInOut} from 'svelte/easing';

    import classnames from 'classnames';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';

    import {exportGPXRide} from '@js/actions/exporterActions';

    import I18n from '@js/modules/translations';
    import * as Utils from '@js/modules/utils';

    import {ElevationRideTypes, RideLevels, RouteTypeToRideType} from '@js/components/modules/constants';
    import Notification from '@js/components/layouts/notification';

    import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';
    import MapBuilder from '@js/modules/maps/MapBuilder';
    import MapToolbar from '@js/components/maps/MapToolbar.svelte';
    import MapLayers from '@js/components/maps/MapLayers.svelte';
    import MapLoader from '@js/components/maps/MapLoader.svelte';
    import MapDataOutline from '@js/components/maps/MapDataOutline.svelte';
    import MapHelper from '@js/components/maps/MapHelper.svelte';
    import MapElevationButton from '@js/components/maps/MapElevationButton.svelte';
    import MapElevationGraph from '@js/components/maps/MapElevationGraph.svelte';
    import MapElevationComputation from '@js/components/maps/MapElevationComputation.svelte';
    import MapSlopeLegend from '@js/components/maps/MapSlopeLegend.svelte';
    import MapSurfaceLegend from '@js/components/maps/MapSurfaceLegend.svelte';

    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';
    import Computation from '@js/modules/computation';

    const {
        mapId,
        class: className,
        rideId,
        reusePreviousTracks = true,
        initialMarkerData,
        initialMapPoints,
        onDistanceChange
    } = $props();

    const TIME_STORAGE_KEY = 'MapTimeStore';
    const WEIGHT_STORAGE_KEY = 'MAPPLANNER-userWeight';

    const _getUserWeight = () => {
        return window.currentUserWeight || StorageService.getItem(WEIGHT_STORAGE_KEY);
    };

    let _previousMapDistance = 0;
    let _previousMapLocationValue;
    let _previousUnit = userStore.unit;
    let _previousRideType = mapStore.currentRideType;
    let _hasInitialElevation = false;

    let isShowElevation = $state(false);
    let elevationData = $state();
    let lastCoordinateElevations = $state();

    const _initialMapPoints = () => {
        if (initialMapPoints) {
            if (Utils.is().isString(initialMapPoints)) {
                const stringPoints = initialMapPoints.split(';');
                return stringPoints.map((stringPoint) => {
                    const point = stringPoint.split(',');

                    if (point.length > 2) {
                        return {
                            type: point[0],
                            latitude: parseFloat(point[1]),
                            longitude: parseFloat(point[2]),
                            delta_distance: point[4] && parseFloat(point[3]),
                            delta_time: point[4] && parseFloat(point[4]),
                            delta_pause: point[5] && parseFloat(point[5])
                        };
                    } else {
                        return null;
                    }
                }).compact();
            } else {
                return initialMapPoints;
            }
        } else {
            return null;
        }
    };

    const _serializeMap = (options = {}) => {
        if (!mapStore.map) {
            return;
        }

        if (mapStore.map.hasOnlyOnePoint()) {
            isShowElevation = false;

            Notification.error(I18n.t('js.maps.errors.one_point.text'));
            return false;
        }

        // check for a map, serialize the points
        const hasActivityData = mapStore.map.hasActivityData();
        if (hasActivityData) {
            const serializedPoints = mapStore.map.serializePoints();

            return {
                serializedPoints: serializedPoints,
                staticMap: options.staticMap ? mapStore.map.staticMapUrl() : undefined
            };
        } else {
            return false;
        }
    };

    const _computeElevation = () => {
        if (!mapStore.map || !mapStore.currentDistance) {
            return;
        }

        mapStore.map.computeElevation((coordinateElevations, simplifiedCoordinateElevations) => {
            const elevationApsis = Computation.computeElevationApsis(simplifiedCoordinateElevations.map((c) => c[1]));

            const totalDistance = simplifiedCoordinateElevations[simplifiedCoordinateElevations.length - 1]?.[0] - simplifiedCoordinateElevations[0]?.[0];
            if (totalDistance > 0) {
                const totalElevationChange = simplifiedCoordinateElevations.reduce((sum, curr, idx, arr) => {
                    if (idx === 0) return 0;
                    return sum + Math.abs(curr[1] - arr[idx - 1][1]);
                }, 0);
                elevationApsis.gradeAvg = Math.round(totalElevationChange / totalDistance * 1000) / 10;
            }

            mapStore.elevationApsis = elevationApsis;

            mapStore.currentLevel = RideLevels[Computation.estimateLevel(mapStore.currentRideType, mapStore.currentDistance, coordinateElevations.map((coordEl) => [coordEl.deltaDistance, coordEl.elevation]))];

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.currentCalories = Computation.estimateCalories(mapStore.currentRideType, mapStore.currentDuration, mapStore.currentDistance, elevationApsis.positiveElevation, _getUserWeight());
            } else {
                mapStore.currentCalories = Computation.estimateCalories(mapStore.currentRideType, mapStore.currentDuration, mapStore.currentDistance, 0, _getUserWeight());
            }

            mapStore.map.updatePointsWithElevation(coordinateElevations);

            lastCoordinateElevations = coordinateElevations;
            mapStore.map.applySlopePercentageIfEnabled(coordinateElevations);

            if (coordinateElevations.length) {
                elevationData = {
                    positiveElevation: mapStore.elevationApsis.positiveElevation,
                    negativeElevation: mapStore.elevationApsis.negativeElevation,
                    minimumAltitude: mapStore.elevationApsis.minimumAltitude,
                    maximumAltitude: mapStore.elevationApsis.maximumAltitude,
                    charts: {
                        elevation: true,
                        speed: false,
                        pace: false
                    },
                    elevation: {
                        xUnits: 'km',
                        yUnits: 'm'
                    },
                    points: coordinateElevations.map((coordEl) => ({
                        distance: coordEl.totalDistance / 1000,
                        elevation: coordEl.elevation,
                        lat: coordEl.lat,
                        lng: coordEl.lng
                    }))
                };
            }
        });
    };

    const _handleMapDistanceChange = (newDistanceInKm) => {
        if (mapId === 'home-map' && !_previousMapDistance && !!newDistanceInKm) {
            AnalyticsService.trackHomePathStarted();
        }

        const formattedDistance = Math.round(newDistanceInKm * 1000);

        if (mapStore.currentDistance === formattedDistance) {
            return;
        }

        if (newDistanceInKm === 0) {
            mapStore.durationComputed = true;
            mapStore.currentDuration = 0;
            mapStore.speedComputed = true;
            mapStore.currentSpeed = 0;
            mapStore.currentLevel = undefined;

            if (ElevationRideTypes.includes(mapStore.currentRideType)) {
                mapStore.elevationApsis = {
                    positiveElevation: null,
                    negativeElevation: null,
                    minimumAltitude: null,
                    maximumAltitude: null,
                    gradeAvg: null
                };
            }
        }

        if (onDistanceChange) {
            onDistanceChange(formattedDistance);
        } else {
            mapStore.currentDistance = formattedDistance;

            if (mapStore.currentDistance) {
                if (mapStore.currentDistance && _previousMapDistance !== mapStore.currentDistance) {
                    _previousMapDistance = mapStore.currentDistance;
                }
            }
        }

        _computeElevation();
    };

    const _handleSnapToRoadChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.setSnapToRoad(checked);
    };

    const _handleStartPointChange = (coordinates) => {
        mapStore.startPointLocation = coordinates;
    };

    const _handleRouteTypeChange = (routeType) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.currentRideType = RouteTypeToRideType[routeType];

        mapStore.map.setRouteType(routeType);
    };

    const _handleBackToHomeClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.backToHome();
    };

    const _handleUndoClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.undo();
    };

    const _handleClearClick = () => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.clearMap(() => {
            isShowElevation = false;
            elevationData = undefined;
            lastCoordinateElevations = undefined;
            mapStore.isSlopePercentageEnabled = false;
            mapStore.isSurfaceTypeEnabled = false;

            StorageService.removeItem(TIME_STORAGE_KEY);
        }, {
            noPopup: userStore.isConnected
        });
    };

    const _handleSlopePercentageChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isSlopePercentageEnabled = checked;
        if (checked) {
            mapStore.isSurfaceTypeEnabled = false;
        }
        mapStore.map.toggleSlopePercentage(checked, lastCoordinateElevations);
    };

    const _handleSurfaceTypeChange = (checked) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.isSurfaceTypeEnabled = checked;
        if (checked) {
            mapStore.isSlopePercentageEnabled = false;
        }
        mapStore.map.toggleSurfaceType(checked);
    };

    const _handleExportGPXClick = () => {
        if (!mapStore.map) {
            return;
        }

        const serializedData = _serializeMap();
        if (serializedData) {
            const serializedMapPoints = serializedData.serializedPoints;

            exportGPXRide(serializedMapPoints);
        }
    };

    const _handleMapStyleChange = (mapStyle) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.setMapStyle(mapStyle);
    };

    const _handleHighlightMapElement = (element) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.highlightMapElement(element);
    };

    const _handleFontSizeChange = (delta) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.changeMapFontSize(delta);
    };

    const _handleElevationClick = (checked) => {
        if (!mapStore.map) {
            return;
        }

        const newValue = typeof checked === 'boolean' ? checked : !isShowElevation;

        if (window.isMetricsEnabled && newValue) {
            AnalyticsService.trackComputeElevationClick('Home page');
        }

        isShowElevation = newValue;
    };

    const _setMapLocation = (bbox, center, placeType, name) => {
        if (!mapStore.map) {
            return;
        }

        mapStore.map.goToLocation(bbox, center, name, {
            showPositionMarker: !name,
            // Use address our street or user geolocation as starting point
            useStartMarker: ['address', 'street', 'geolocation', 'house'].includes(placeType)
        });
    };

    // OPTIMIZE: add geolocation
    // _handleGeolocateClick = (event) => {
    //     event.preventDefault();
    //
    //     AnalyticsService.trackGeolocationClick('Home page');
    //
    //     if (mapStore.map) {
    //         mapStore.map.onGeolocation();
    //
    //         this.setState({
    //             isGeolocating: true
    //         });
    //     }
    // };

    const _loadHomeMap = () => {
        MapBuilder.createMap(
            MAP_CLASS.BUILD,
            [
                MAP_MODULES.TOOLBAR,
                MAP_MODULES.COMPUTE_ROAD,
                MAP_MODULES.DRAW_LINE,
                MAP_MODULES.RIDE_STORE,
                MAP_MODULES.RIDE_SHARE,
                MAP_MODULES.RIDE_POIS,
                MAP_MODULES.ELEVATION_COMPUTATION,
                MAP_MODULES.GRAPH,
                MAP_MODULES.HELPER,
                MAP_MODULES.GEOLOCATION
            ],
            {
                mapId,
                rideId,
                reusePreviousTracks,
                initialPoints: _initialMapPoints(),
                initialLocation: initialMarkerData ? [initialMarkerData.lat, initialMarkerData.lng] : mapStore.mapLocation,
                reusePosition: !initialMarkerData && !mapStore.mapLocation,
                highlightedPlaceName: initialMarkerData?.name,
                useSearchMarker: !!initialMarkerData,
                hideMapScale: true,
                defaultZoom: 12
            }
        )
            .then((map) => {
                if (!map) {
                    return;
                }

                mapStore.map = map;

                mapStore.map.onDistanceUpdated(_handleMapDistanceChange);

                mapStore.map.onStartPointChanged(_handleStartPointChange);

                // Compute the initial elevation once the map and its imported track are
                // ready. Required in edit mode where the distance is preset, so the
                // distance callback never fires for the initial (unmodified) track.
                mapStore.map.onMapLoaded(() => {
                    if (_hasInitialElevation || !mapStore.currentDistance || !mapStore.map.hasActivityData()) {
                        return;
                    }

                    _hasInitialElevation = true;

                    _computeElevation();

                    if (!mapStore.startPointLocation) {
                        const firstPoint = mapStore.map._mapModel?.linePoints?.[0]?.latLng;
                        if (firstPoint) {
                            mapStore.startPointLocation = firstPoint;
                        }
                    }
                });

                userStore.subscribe((userState) => {
                    if (userState.unit !== _previousUnit) {
                        _previousUnit = userState.unit;

                        mapStore.map.onUnitChanged(userState.unit);
                    }
                });
            });
    };

    onMount(() => {
        if (!mapStore.map) {
            _loadHomeMap();
        }

        return () => {
            if (mapStore.map) {
                mapStore.map.destroy();
                mapStore.map = null;
            }
        };
    });

    $effect(() => {
        if ($mapStore.mapLocation && ($mapStore.mapLocation.bbox || $mapStore.mapLocation.center) && _previousMapLocationValue !== $mapStore.mapLocation.value) {
            _previousMapLocationValue = $mapStore.mapLocation.value;

            _setMapLocation($mapStore.mapLocation.bbox, $mapStore.mapLocation.center, $mapStore.mapLocation.placeType, $mapStore.mapLocation.name);
        }
    });

    // Recompute elevation when ride type changes (for calories, level, etc.)
    $effect(() => {
        if ($mapStore.currentRideType && _previousRideType !== $mapStore.currentRideType) {
            _previousRideType = $mapStore.currentRideType;

            _computeElevation();
        }
    });

</script>

<div id={mapId}
     class="my-4">
    <div class="map-area relative overflow-visible">
        <div id={`${mapId}-map`}
             class={classnames('map-canvas relative', className)}>
            <MapToolbar isSnapToRoadChecked={$userStore.featureFlags.map_snap_to_road_enabled}
                        maxRouteTypeChange={$userStore.featureFlags.map_max_route_type}
                        isPoiModeChecked={false}
                        hasElevationData={!!lastCoordinateElevations?.length}
                        isSlopePercentageEnabled={$mapStore.isSlopePercentageEnabled}
                        onSlopePercentageChange={_handleSlopePercentageChange}
                        isSurfaceTypeEnabled={$mapStore.isSurfaceTypeEnabled}
                        onSurfaceTypeChange={_handleSurfaceTypeChange}
                        isElevationEnabled={isShowElevation}
                        onElevationToggle={_handleElevationClick}
                        onSnapToRoadChange={_handleSnapToRoadChange}
                        onRouteTypeChange={_handleRouteTypeChange}
                        onBackToHomeClick={_handleBackToHomeClick}
                        onUndoClick={_handleUndoClick}
                        onClearClick={_handleClearClick}
                        onExportGPXClick={_handleExportGPXClick}/>

            {#if $userStore.featureFlags.map_available_layers?.length}
                <MapLayers mapLayersAvailable={$userStore.featureFlags.map_available_layers}
                           onMapStyleChange={_handleMapStyleChange}
                           onHighlightMapElement={_handleHighlightMapElement}
                           onFontSizeChange={_handleFontSizeChange}/>
            {/if}

            <MapHelper/>

            {#if $mapStore.isSlopePercentageEnabled}
                <MapSlopeLegend/>
            {/if}

            {#if $mapStore.isSurfaceTypeEnabled}
                <MapSurfaceLegend/>
            {/if}
        </div>

        <MapDataOutline estimateDuration={true}/>

        <MapLoader/>
    </div>

    <div class="mt-6"></div>

    <MapElevationComputation/>

    {#if isShowElevation}
        <div transition:slide={{duration: 300, easing: cubicInOut}}>
            <MapElevationGraph map={$mapStore.map}
                               currentRideType={$mapStore.currentRideType}
                               elevationData={elevationData}
                               warningHelper={true}/>
        </div>
    {/if}

    <MapElevationButton onElevationToggle={_handleElevationClick}/>
</div>
